/**
 * Who may use LingoPing now: the paid subscription with a 14-day trial
 * (Docs/plans/SUBSCRIPTION_PLAN.md). The server decides (SQL `access_state(user)`); every device
 * gets the answer with each `sync` answer (`access`) and from a `402 access_locked` refusal, and
 * shows it. Nothing here decides access on a device.
 */
/** The one plan for now. Prices include VAT; Paddle charges the same everywhere. */
export const SUBSCRIPTION_PLANS = {
    monthly: { amountCents: 599, currency: 'EUR', interval: 'month' },
};
export const TRIAL_DAYS = 14;
/** "€5.99" (English interface). */
export function formatPrice(amountCents, currency = 'EUR') {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(amountCents / 100);
}
const STATES = ['active', 'grant', 'trial', 'locked'];
const REASONS = [
    'subscription',
    'grant',
    'trial',
    'payment_failed',
    'subscription_ended',
    'trial_ended',
];
const STATUSES = ['trialing', 'active', 'past_due', 'paused', 'canceled'];
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const dateOrNull = (v) => typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null;
/** The server's answer checked field by field; anything malformed is null (show nothing). */
export function parseAccessState(raw) {
    if (!isObject(raw))
        return null;
    const { state, reason } = raw;
    const trialEndsAt = dateOrNull(raw.trial_ends_at);
    if (typeof state !== 'string' || !STATES.includes(state))
        return null;
    if (typeof reason !== 'string' || !REASONS.includes(reason))
        return null;
    if (!trialEndsAt)
        return null;
    let subscription = null;
    if (isObject(raw.subscription)) {
        const s = raw.subscription;
        if (typeof s.status === 'string' && STATUSES.includes(s.status)) {
            subscription = {
                status: s.status,
                plan: typeof s.plan === 'string' ? s.plan : 'monthly',
                current_period_end: dateOrNull(s.current_period_end),
                cancel_at: dateOrNull(s.cancel_at),
            };
        }
    }
    return {
        enforced: raw.enforced === true,
        state: state,
        reason: reason,
        until: dateOrNull(raw.until),
        trial_ends_at: trialEndsAt,
        locked_since: dateOrNull(raw.locked_since),
        subscription,
    };
}
/** Whether the learner is locked out now (only once payments are switched on). */
export function isLocked(access) {
    return !!access && access.enforced && access.state === 'locked';
}
/** Whether to show the trial line ("Trial: 9 days left · Subscribe"). */
export function showsTrial(access) {
    return !!access && access.enforced && access.state === 'trial';
}
const DAY_MS = 24 * 60 * 60 * 1000;
/** Whole days left in the trial, rounded up (the last day counts as 1); null outside a trial. */
export function trialDaysLeft(access, now = new Date()) {
    if (!access || access.state !== 'trial')
        return null;
    const ms = Date.parse(access.trial_ends_at) - now.getTime();
    return ms <= 0 ? 0 : Math.ceil(ms / DAY_MS);
}
/** Whether the learner already pays (or subscribed during the trial): no "Subscribe" prompts. */
export function hasSubscription(access) {
    return access?.state === 'active';
}
